import { Lead, CreateLeadData, UpdateLeadData, LeadNote, Task, LeadSource } from '@/types/lead';
import { Activity } from '@/types/activity';
import {
  SalesPeriod,
  TrendRange,
  TrendGranularity,
  LeaderboardMetric,
  SalesSummaryResponse,
  SalesByExecutiveResponse,
  SalesTrendsResponse,
  ConversionFunnelResponse,
  LeaderboardResponse,
  QuickStatsResponse,
} from '@/types/salesAnalytics';
import {
  DashboardPeriod,
  SalesDashboardResponse,
  QuickStatsResponse as SalesDashboardQuickStatsResponse,
} from '@/types/salesDashboard';

// Re-export types for external use
export type { Task, Lead, LeadNote, CreateLeadData, UpdateLeadData, Activity, LeadSource };

export interface LeadActivity {
  id: string;
  leadId: string;
  type: 'CALL' | 'MESSAGE' | 'NOTE' | 'FILE' | 'STATUS_CHANGE' | 'STAGE_CHANGE';
  content: string;
  timestamp: string;
  createdBy: string;
  metadata?: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'EXECUTIVE' | 'MANAGER' | 'GROUP_MANAGER' | 'CEO';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  phone?: string;
  department?: string;
  position?: string;
  avatar?: string;
  managerId?: string;
  manager?: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  subordinates?: {
    id: string;
    name: string;
    email: string;
    role: string;
  }[];
  lastLoginAt?: string;
  loginCount: number;
  isEmailVerified: boolean;
  leads?: Lead[];
  tasks?: Task[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  _count?: {
    leads: number;
    tasks: number;
    subordinates: number;
    activities: number;
  };
}

export interface CreateTaskData {
  title: string;
  description?: string;
  dueDate: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  userId: string;
}

export interface UpdateTaskData {
  title?: string;
  description?: string;
  dueDate?: string;
  status?: 'PENDING' | 'COMPLETED' | 'OVERDUE';
  priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  userId?: string;
}

export interface Risk {
  code: string;
  label: string;
  description: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  color: string;
  icon: string;
  details: string;
}

export interface Recommendation {
  type: 'ACTION' | 'URGENT';
  title: string;
  description: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  suggestedActions: string[];
}

export interface RiskAssessment {
  lead: Lead;
  risks: Risk[];
  recommendations: Recommendation[];
  riskCount: number;
  highestSeverity: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface RiskStatistics {
  totalLeads: number;
  leadsWithRisks: number;
  riskBreakdown: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  riskTypeBreakdown: Record<string, number>;
}

export interface DashboardData {
  leads?: Lead[];
  users?: User[];
  teamMembers?: User[];
  managers?: User[];
  stats: {
    totalLeads?: number;
    totalValue?: number;
    wonLeads?: number;
    wonValue?: number;
    totalTeamLeads?: number;
    totalTeamValue?: number;
    totalManagers?: number;
    totalUsers?: number;
    leadsByStage?: {
      New: number;
      Contacted: number;
      Discussion: number;
      Proposal: number;
      Negotiation: number;
      Won: number;
      Lost: number;
    };
  };
}

export interface LeadMetrics {
  closures: {
    expectedToday: { count: number; value: number };
    expectedTomorrow: { count: number; value: number };
    closedToday: { count: number; value: number };
    closedThisWeek: { count: number; value: number };
    missed: { count: number; value: number };
  };
  followUps: {
    today: { count: number; percentage: number };
    tomorrow: { count: number; percentage: number };
    missed: { count: number; percentage: number };
  };
  volume: {
    allLeads: number;
    allFollowUps: number;
    addedToday: number;
    upcoming: number;
    selfAssigned: number;
    assignedByOthers: number;
    reassignedToday: number;
  };
  value: {
    total: number;
    converted: number;
    convertedToday: number;
    addedToday: number;
    assignedToday: number;
    highestDeal: number;
    avgDealSize: number;
    dealsOver50K: number;
  };
  funnel: {
    freshLead: { count: number; value: number };
    demoBooked: { count: number; value: number };
    proposalSent: { count: number; value: number };
    followUpOngoing: { count: number; value: number };
    negotiation: { count: number; value: number };
    closedWon: { count: number; value: number };
    closedLost: { count: number; value: number };
  };
  performance: {
    executives: Array<{
      id: string;
      name: string;
      leadCount: number;
      pipelineValue: number;
      conversionRate: number;
      avgClosureDays: number;
    }>;
  };
}

// IMPORTANT: REACT_APP_API_URL must be set for production builds
// Development fallback to localhost, but warn if not set
// Smart API URL: use env var if set, otherwise auto-detect based on current hostname
// This handles: localhost dev, ngrok tunnels, and production builds
const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    // Accessed from ngrok or production — use same-origin proxy (CRACO proxies /api to backend)
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:5000/api';
})();

// Add debug logging for API calls
const debugLog = (method: string, url: string, data?: any) => {
  console.log(`🌐 API ${method}:`, url, data ? { data } : '');
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };

  // Debug log for authentication
  if (!token) {
    console.warn('⚠️ No auth token found in localStorage');
  } else {
    console.log('🔑 Auth token found:', token.substring(0, 20) + '...');
  }
  console.log('📤 Request headers:', headers);

  return headers;
};

// Handle auth errors: 401 = expired session (redirect to login), 403 = forbidden (show message)
const handleAuthError = (response: Response) => {
  const isAuthEndpoint = response.url.includes('/auth/login') || response.url.includes('/auth/register');
  if (isAuthEndpoint) return response;

  if (response.status === 401) {
    console.warn('🔒 Session expired (HTTP 401) — redirecting to login');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    if (!window.location.pathname.startsWith('/auth') && !window.location.pathname.startsWith('/forgot-password')) {
      window.location.href = '/auth';
    }
  } else if (response.status === 403) {
    console.warn('🚫 Permission denied (HTTP 403) for:', response.url);
    // Don't redirect — let the caller handle the 403 with a user-friendly message
  }
  return response;
};

// Maps HTTP status codes to user-friendly error messages
const getFriendlyErrorMessage = (status: number, fallback: string): string => {
  if (status === 403) return 'You don\'t have permission for this action';
  if (status === 404) return 'The requested resource was not found';
  if (status >= 500) return 'Server error — please try again later';
  return fallback;
};

export const api = {
  // Authentication
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    const data = await response.json();
    // Handle the response structure: { success: true, data: { user, token } }
    if (data.success && data.data) {
      return data.data;
    }
    // Handle legacy structure: { user, token }
    return data;
  },

  async register(data: {
    name: string;
    email: string;
    password: string;
    role?: string;
  }): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Registration failed');
    }

    return response.json();
  },

  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to get current user');
    }

    const data = await response.json();
    // Handle the response structure: { success: true, data: { user } }
    if (data.success && data.data && data.data.user) {
      return data.data.user;
    }
    // Handle other structures
    return data.data || data;
  },

  async logout(): Promise<void> {
    localStorage.removeItem('authToken');
  },

  // Client Health
  async getClientHealth(): Promise<import('../types/clientHealth').ClientHealthResponse> {
    const response = await fetch(`${API_BASE_URL}/client-health`, { headers: getAuthHeaders() });
    handleAuthError(response);
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch client health'));
    }
    const result = await response.json();
    return result.success && result.data ? result.data : result;
  },

  // Leads
  async getLeads(params?: {
    query?: string;
    stage?: string;
    status?: string;
    assignedTo?: string;
    sortBy?: string;
    sortOrder?: string;
    riskFlag?: string;
    createdAfter?: string;
    createdBefore?: string;
    minValue?: number;
    maxValue?: number;
    // Advanced filter parameters
    closureDateFrom?: string;
    closureDateTo?: string;
    lastContactFrom?: string;
    lastContactTo?: string;
    riskLevel?: string;
  }): Promise<Lead[]> {
    const url = new URL(`${API_BASE_URL}/leads`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      });
    }

    const response = await fetch(url.toString(), {
      headers: getAuthHeaders(),
    });
    handleAuthError(response);
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch leads'));
    }
    const result = await response.json();
    // Handle response structure: { success: true, data: [...] }
    if (result.success && result.data) {
      return result.data;
    }
    // Handle direct array response (legacy)
    return result;
  },

  // Get single lead
  async getLead(id: string): Promise<Lead> {
    console.log('🔍 API: Fetching lead details for ID:', id);
    console.log('🔗 API URL:', `${API_BASE_URL}/leads/${id}`);
    
    const response = await fetch(`${API_BASE_URL}/leads/${id}`, {
      headers: getAuthHeaders(),
    });
    
    console.log('📡 Lead details response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch lead:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`Failed to fetch lead: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    console.log('✅ Lead details result:', result);
    return result.data || result;
  },

  // Create a new lead
  async createLead(data: CreateLeadData): Promise<Lead> {
    const response = await fetch(`${API_BASE_URL}/leads`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    handleAuthError(response);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Backend returns either 'error' (generic) or 'message' (validation) field
      const firstValidationError = errorData.errors?.[0]?.message;
      throw new Error(firstValidationError || errorData.error || errorData.message || 'Failed to create lead');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // Update a lead
  async updateLead(id: string, data: UpdateLeadData): Promise<Lead> {
    console.log('🔄 API: Starting updateLead request');
    console.log('📝 Lead ID:', id);
    console.log('📦 Update data:', data);
    console.log('🔗 Full URL:', `${API_BASE_URL}/leads/${id}`);
    
    const headers = getAuthHeaders();
    console.log('📤 Request headers:', headers);
    
    debugLog('PATCH', `${API_BASE_URL}/leads/${id}`, data);
    
    try {
      console.log('🚀 Making PATCH request...');
      const response = await fetch(`${API_BASE_URL}/leads/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      });
      
      console.log('📡 PATCH Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        type: response.type,
        headers: Object.fromEntries(response.headers.entries())
      });
      handleAuthError(response);

      if (!response.ok) {
        let errorMessage = `Failed to update lead (${response.status})`;
        try {
          const errorBody = await response.json();
          errorMessage = errorBody.message || errorBody.error || errorMessage;
        } catch (e) {
          // body not JSON — keep default message
        }
        console.error('❌ Update lead failed:', response.status, errorMessage);
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('✅ Lead updated successfully - Full Response:', result);
      return result;
    } catch (error) {
      if (error instanceof TypeError) {
        console.error('❌ Network Error in updateLead:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
          url: `${API_BASE_URL}/leads/${id}`,
          data,
          possibleCauses: [
            'CORS policy blocking request',
            'Backend server not responding', 
            'Network connectivity issue',
            'Invalid URL or port'
          ]
        });
      } else {
        console.error('❌ Other Error in updateLead:', error);
      }
      throw error;
    }
  },

  // Delete a lead
  async deleteLead(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/leads/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete lead');
    }
  },

  // Get lead activities
  async getLeadActivities(leadId: string): Promise<LeadActivity[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/activities`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead activities');
    }
    return response.json();
  },

  // Add new activity
  async addActivity(leadId: string, data: { type: string; content: string; createdBy: string }): Promise<LeadActivity> {
    debugLog('POST', `${API_BASE_URL}/leads/${leadId}/activities`, data);
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/activities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Add activity failed:', response.status, errorText);
      throw new Error(`Failed to add activity: ${response.status} ${errorText}`);
    }
    const result = await response.json();
    console.log('✅ Activity added successfully:', result);
    return result;
  },

  async createLeadActivity(leadId: string, data: { 
    type: LeadActivity['type']; 
    content: string; 
    createdBy?: string;
    metadata?: string;
  }): Promise<LeadActivity> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/activities`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...data,
        createdBy: data.createdBy || 'system'
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create lead activity');
    }

    return response.json();
  },

  // Delete activity
  async deleteActivity(activityId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/activities/${activityId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete activity');
    }
  },

  // Get all users
  async getUsers(): Promise<User[]> {
    const response = await fetch(`${API_BASE_URL}/users`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch users'));
    }
    const result = await response.json();
    // Handle response structure: { success: true, data: [...] }
    if (result.success && result.data) {
      return result.data;
    }
    // Handle direct array response (legacy)
    return result;
  },

  // Get user by ID
  async getUser(id: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    return response.json();
  },

  // Get user's leads
  async getUserLeads(userId: string): Promise<Lead[]> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/leads`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user leads');
    }
    return response.json();
  },

  // Get dashboard data by role
  async getDashboardData(role: string, userId?: string): Promise<DashboardData> {
    const url = userId 
      ? `${API_BASE_URL}/dashboard/${role}?userId=${userId}`
      : `${API_BASE_URL}/dashboard/${role}`;
    
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch dashboard data'));
    }
    return response.json();
  },

  // Task Management API

  // Get all tasks for a user
  async getUserTasks(userId: string, status?: string, leadId?: string, dueDateFrom?: string, dueDateTo?: string): Promise<Task[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (leadId) params.append('leadId', leadId);
    if (dueDateFrom) params.append('dueDateFrom', dueDateFrom);
    if (dueDateTo) params.append('dueDateTo', dueDateTo);

    const response = await fetch(`${API_BASE_URL}/users/${userId}/tasks?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user tasks');
    }
    return response.json();
  },

  // Get tasks for a specific lead
  async getLeadTasks(leadId: string): Promise<Task[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/tasks`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead tasks');
    }
    return response.json();
  },

  // Create a new task
  async createTask(leadId: string, data: { title: string; description?: string; dueDate: string; priority: string }): Promise<Task> {
    debugLog('POST', `${API_BASE_URL}/leads/${leadId}/tasks`, data);
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/tasks`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Create task failed:', response.status, errorText);
      throw new Error(`Failed to create task: ${response.status} ${errorText}`);
    }
    const result = await response.json();
    console.log('✅ Task created successfully:', result);
    return result;
  },

  // Create a standalone task (without requiring a lead)
  async createStandaloneTask(data: { title: string; description?: string; dueDate: string; priority: string; leadId?: string }): Promise<Task> {
    debugLog('POST', `${API_BASE_URL}/tasks`, data);
    const response = await fetch(`${API_BASE_URL}/tasks`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Create standalone task failed:', response.status, errorText);
      throw new Error(`Failed to create task: ${response.status} ${errorText}`);
    }
    const result = await response.json();
    console.log('✅ Standalone task created successfully:', result);
    return result.data || result;
  },

  // Update a task
  async updateTask(taskId: string, data: UpdateTaskData): Promise<Task> {
    const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to update task');
    }
    return response.json();
  },

  // Reassign a task to another user
  async reassignTask(taskId: string, userId: string): Promise<Task> {
    const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      throw new Error('Failed to reassign task');
    }
    return response.json();
  },

  // Delete a task
  async deleteTask(taskId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete task');
    }
  },

  // Lead Notes API

  // Get lead notes
  async getLeadNotes(leadId: string): Promise<LeadNote[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/notes`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead notes');
    }
    return response.json();
  },

  // Add new note
  async addNote(leadId: string, content: string): Promise<LeadNote> {
    debugLog('POST', `${API_BASE_URL}/leads/${leadId}/notes`, { content });
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/notes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ content }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Add note failed:', response.status, errorText);
      throw new Error(`Failed to add note: ${response.status} ${errorText}`);
    }
    const result = await response.json();
    console.log('✅ Note added successfully:', result);
    return result;
  },

  // Risk Assessment API

  // Get risk assessment for all leads
  async getRiskAssessment(): Promise<RiskAssessment[]> {
    const response = await fetch(`${API_BASE_URL}/risks/assessment`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch risk assessment');
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch risk assessment');
    }
    return result.data || [];
  },

  // Get risk assessment for specific lead
  async getLeadRiskAssessment(leadId: string): Promise<RiskAssessment> {
    const response = await fetch(`${API_BASE_URL}/risks/leads/${leadId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead risk assessment');
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch lead risk assessment');
    }
    return result.data;
  },

  // Get risk statistics
  async getRiskStatistics(): Promise<RiskStatistics> {
    const response = await fetch(`${API_BASE_URL}/risks/statistics`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch risk statistics');
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch risk statistics');
    }
    return result.data;
  },

  // Update risk flags for all leads
  async updateRiskFlags(): Promise<{ message: string; updatedLeads: number }> {
    const response = await fetch(`${API_BASE_URL}/risks/update-flags`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to update risk flags');
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to update risk flags');
    }
    return result.data;
  },

  // User Management API

  // Get all users with filtering and pagination
  async getUsersWithFilters(filters?: {
    search?: string;
    role?: string;
    status?: string;
    department?: string;
    managerId?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ users: User[]; pagination: any }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
    }
    
    const response = await fetch(`${API_BASE_URL}/users?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    const data = await response.json();

    // Handle multiple response formats from backend
    // Format 1: { success: true, data: [...], meta: {...} } (current backend format)
    if (data.success && Array.isArray(data.data)) {
      return {
        users: data.data,
        pagination: {
          page: 1,
          limit: data.data.length,
          total: data.meta?.count || data.data.length,
          pages: 1
        }
      };
    }

    // Format 2: Direct array
    if (Array.isArray(data)) {
      return {
        users: data,
        pagination: {
          page: 1,
          limit: data.length,
          total: data.length,
          pages: 1
        }
      };
    }

    // Format 3: { users: [...], pagination: {...} }
    if (data.users && Array.isArray(data.users)) {
      return data;
    }

    // Fallback: empty response
    return {
      users: [],
      pagination: { page: 1, limit: 20, total: 0, pages: 0 }
    };
  },

  // Get user by ID with full details
  async getUserById(id: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }
    return response.json();
  },

  // Create new user
  async createUser(data: {
    name: string;
    email: string;
    password: string;
    role?: string;
    status?: string;
    phone?: string;
    department?: string;
    position?: string;
    managerId?: string;
    avatar?: string;
  }): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message =
        errorBody?.error ||
        errorBody?.message ||
        (Array.isArray(errorBody?.errors) && errorBody.errors[0]?.message) ||
        'Failed to create user';
      throw new Error(message);
    }
    return response.json();
  },

  // Update user
  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to update user');
    }
    return response.json();
  },

  // Delete user
  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete user');
    }
  },

  // Change user role
  async changeUserRole(id: string, role: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/role`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ role }),
    });
    if (!response.ok) {
      throw new Error('Failed to change user role');
    }
    return response.json();
  },

  // Promote user
  async promoteUser(id: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/promote`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to promote user');
    }
    return response.json();
  },

  // Demote user
  async demoteUser(id: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/demote`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to demote user');
    }
    return response.json();
  },

  // Get team hierarchy
  async getTeamHierarchy(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/hierarchy`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch team hierarchy');
    }
    return response.json();
  },

  // Get user statistics
  async getUserStatistics(id: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/users/${id}/statistics`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user statistics');
    }
    return response.json();
  },

  // Get all roles
  async getRoles(): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/users/roles`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch roles');
    }
    return response.json();
  },

  // Get all statuses
  async getStatuses(): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/users/statuses`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch statuses');
    }
    return response.json();
  },

  // Bulk update users
  async bulkUpdateUsers(userIds: string[], updateData: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/users/bulk-update`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userIds, updateData }),
    });
    if (!response.ok) {
      throw new Error('Failed to bulk update users');
    }
    return response.json();
  },

  // ─────────────────────────────────────────────────────────────────────
  // ACCOUNT APPROVAL FLOW
  // Spec: docs/superpowers/specs/2026-05-18-signup-approval-welcome-email.md
  // ─────────────────────────────────────────────────────────────────────

  // List pending self-service signups (CEO/GROUP_MANAGER only)
  async getPendingUsers(): Promise<Array<{ id: string; name: string; email: string; phoneNumber: string | null; role: string; status: string; isApproved: boolean; createdAt: string }>> {
    const res = await fetch(`${API_BASE_URL}/users/queue/pending`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch pending users');
    const body = await res.json();
    return body.data || [];
  },

  // Approve a pending signup
  async approveUser(id: string, opts: { generatePasswordReset?: boolean } = {}): Promise<{ user: any; alreadyApproved?: boolean; resetUrl?: string | null }> {
    const res = await fetch(`${API_BASE_URL}/users/${id}/approve`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(opts),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to approve user');
    }
    const body = await res.json();
    return body.data;
  },

  // Reject a pending signup (hard delete)
  async rejectUser(id: string, reason?: string): Promise<{ success: boolean; deletedUser: { id: string; name: string; email: string } }> {
    const res = await fetch(`${API_BASE_URL}/users/${id}/reject`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to reject user');
    }
    const body = await res.json();
    return body.data;
  },

  // Get offboard preview (lead and task counts)
  async getOffboardPreview(userId: string): Promise<{ leadCount: number; taskCount: number }> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/offboard-preview`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to get offboard preview');
    }
    const data = await response.json();
    return data.data;
  },

  // Offboard user (reassign leads/tasks and deactivate)
  async offboardUser(userId: string, targetUserId: string): Promise<{ leadsReassigned: number; tasksReassigned: number }> {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/offboard`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ targetUserId }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to offboard user');
    }
    return response.json();
  },

  // ==========================================
  // Lead Sources API
  // ==========================================

  // Get all active lead sources
  async getLeadSources(): Promise<LeadSource[]> {
    const response = await fetch(`${API_BASE_URL}/lead-sources`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead sources');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // Get all lead sources (including inactive) - Admin only
  async getAllLeadSources(): Promise<LeadSource[]> {
    const response = await fetch(`${API_BASE_URL}/lead-sources/all`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch all lead sources');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // Create a new lead source - Admin only
  async createLeadSource(data: { name: string; description?: string; color?: string }): Promise<LeadSource> {
    const response = await fetch(`${API_BASE_URL}/lead-sources`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to create lead source');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // Update a lead source - Admin only
  async updateLeadSource(id: string, data: { name?: string; description?: string; color?: string; isActive?: boolean; sortOrder?: number }): Promise<LeadSource> {
    const response = await fetch(`${API_BASE_URL}/lead-sources/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update lead source');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // Delete (deactivate) a lead source - Admin only
  async deleteLeadSource(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/lead-sources/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to delete lead source');
    }
  },

  // Get lead source statistics
  async getLeadSourceStats(): Promise<{
    sources: Array<{
      id: string | null;
      name: string;
      color: string;
      leadCount: number;
      totalDealValue: number;
    }>;
    totalLeads: number;
    totalDealValue: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/lead-sources/stats`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead source statistics');
    }
    return response.json();
  },

  // Reorder lead sources - Admin only
  async reorderLeadSources(sourceIds: string[]): Promise<LeadSource[]> {
    const response = await fetch(`${API_BASE_URL}/lead-sources/reorder`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ sourceIds }),
    });
    if (!response.ok) {
      throw new Error('Failed to reorder lead sources');
    }
    const json = await response.json();
    return json.data ?? json;
  },

  // Proposal Management API

  // Upload proposal file
  async uploadProposal(leadId: string, file: File, notes?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) {
      formData.append('notes', notes);
    }

    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/proposals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Failed to upload proposal');
    }
    return response.json();
  },

  // Get all proposals for a lead
  async getProposals(leadId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/proposals`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch proposals');
    }
    return response.json();
  },

  // Get proposal by ID
  async getProposal(proposalId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/proposals/${proposalId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch proposal');
    }
    return response.json();
  },

  // Update proposal status
  async updateProposalStatus(proposalId: string, status: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/proposals/${proposalId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      throw new Error('Failed to update proposal status');
    }
    return response.json();
  },

  // Mark proposal as seen
  async markProposalAsSeen(proposalId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/proposals/${proposalId}/seen`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to mark proposal as seen');
    }
    return response.json();
  },

  // Delete proposal
  async deleteProposal(proposalId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/proposals/${proposalId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete proposal');
    }
  },

  // Get proposal statistics
  async getProposalStats(leadId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/proposals/stats`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch proposal stats');
    }
    return response.json();
  },

  // Get lifecycle events
  async getLifecycleEvents(leadId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/lifecycle`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lifecycle events');
    }
    return response.json();
  },

  // Download proposal file
  async downloadProposal(proposalId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/proposals/${proposalId}/download`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to download proposal');
    }
    return response.blob();
  },

  async getLeadMetrics(role?: string, userId?: string, filters?: { timeRange?: string; stage?: string; priority?: string; brand?: string; creator?: string }): Promise<LeadMetrics> {
    const params = new URLSearchParams();
    if (role) params.append('role', role);
    if (userId) params.append('userId', userId);
    if (filters) {
      // Map frontend timeRange values to backend-accepted values
      const timeRangeMap: Record<string, string> = {
        '7d': 'thisWeek',
        '30d': 'thisMonth',
        '90d': 'thisQuarter',
        '1y': 'thisYear',
      };
      const mappedTimeRange = filters.timeRange ? (timeRangeMap[filters.timeRange] || filters.timeRange) : undefined;
      if (mappedTimeRange && mappedTimeRange !== 'all') params.append('timeRange', mappedTimeRange);
      if (filters.stage && filters.stage !== 'all') params.append('stage', filters.stage);
      if (filters.priority && filters.priority !== 'all') params.append('priority', filters.priority);
      if (filters.brand && filters.brand !== 'all') params.append('brand', filters.brand);
      if (filters.creator && filters.creator !== 'all') params.append('creator', filters.creator);
    }

    const response = await fetch(`${API_BASE_URL}/lead-metrics?${params}`, {
      headers: getAuthHeaders(),
    });

    // Gracefully handle 404 - return empty metrics for users with no data
    if (response.status === 404) {
      console.warn('⚠️ Lead metrics endpoint not found (404), returning empty metrics');
      return {
        closures: {
          expectedToday: { count: 0, value: 0 },
          expectedTomorrow: { count: 0, value: 0 },
          closedToday: { count: 0, value: 0 },
          closedThisWeek: { count: 0, value: 0 },
          missed: { count: 0, value: 0 }
        },
        followUps: {
          today: { count: 0, percentage: 0 },
          tomorrow: { count: 0, percentage: 0 },
          missed: { count: 0, percentage: 0 }
        },
        volume: {
          allLeads: 0,
          allFollowUps: 0,
          addedToday: 0,
          upcoming: 0,
          selfAssigned: 0,
          assignedByOthers: 0,
          reassignedToday: 0
        },
        value: {
          total: 0,
          converted: 0,
          convertedToday: 0,
          addedToday: 0,
          assignedToday: 0,
          highestDeal: 0,
          avgDealSize: 0,
          dealsOver50K: 0
        },
        funnel: {
          freshLead: { count: 0, value: 0 },
          demoBooked: { count: 0, value: 0 },
          proposalSent: { count: 0, value: 0 },
          followUpOngoing: { count: 0, value: 0 },
          negotiation: { count: 0, value: 0 },
          closedWon: { count: 0, value: 0 },
          closedLost: { count: 0, value: 0 }
        },
        performance: {
          executives: []
        }
      };
    }

    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch lead metrics'));
    }

    return response.json();
  },

  // Get stage-wise statistics for value totals
  async getStageStats(): Promise<Array<{stage: string; count: number; totalValue: number}>> {
    console.log('📊 API: Fetching stage statistics');
    console.log('🔗 API URL:', `${API_BASE_URL}/leads/stats/stages`);
    
    const response = await fetch(`${API_BASE_URL}/leads/stats/stages`, {
      headers: getAuthHeaders(),
    });
    
    console.log('📡 Stage stats response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch stage statistics:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`Failed to fetch stage statistics: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    console.log('✅ Stage stats result:', result);
    // Support both wrapped {success, data} and bare array formats
    return result.data || result;
  },

  // Upload file for lead
  async uploadLeadFile(leadId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    
    console.log('📤 Uploading file:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      leadId
    });
    
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/files`, {
      method: 'POST',
      headers, // Don't set Content-Type for FormData, let browser set it with boundary
      body: formData,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ File upload failed:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`Failed to upload file: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    console.log('✅ File uploaded successfully:', result);
    return result;
  },

  // Get files for lead
  async getLeadFiles(leadId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/files`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead files');
    }
    return response.json();
  },

  // Activity Feed API

  // Get recent activities
  async getRecentActivities(limit: number = 20): Promise<Activity[]> {
    console.log('🔔 API: Fetching recent activities');
    console.log('🔗 API URL:', `${API_BASE_URL}/activities/recent?limit=${limit}`);

    const response = await fetch(`${API_BASE_URL}/activities/recent?limit=${limit}`, {
      headers: getAuthHeaders(),
    });

    console.log('📡 Recent activities response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch recent activities:', {
        status: response.status,
        statusText: response.statusText,
        errorText
      });
      throw new Error(`Failed to fetch activities: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ Recent activities result:', result);

    // Handle response structure: { success: true, data: { activities: [...] } }
    if (result.success && result.data && result.data.activities) {
      return result.data.activities;
    }

    // Handle direct array response
    if (Array.isArray(result)) {
      return result;
    }

    return [];
  },

  // ==========================================
  // Payment Tracking API
  // ==========================================

  // Get finance dashboard data
  async getFinanceDashboard(filters?: {
    paymentStatus?: string;
    assignedTo?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);

    const response = await fetch(`${API_BASE_URL}/finance/dashboard?${params}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch finance dashboard'));
    }
    const result = await response.json();
    return result.data;
  },

  // Export finance data
  async exportFinanceData(format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/finance/export?format=${format}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to export finance data');
    }
    return response.blob();
  },

  // Get payments for a lead
  async getLeadPayments(leadId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/payments`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch lead payments');
    }
    const result = await response.json();
    return result.data;
  },

  // Record a new payment
  async recordPayment(leadId: string, data: {
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    invoiceNumber?: string;
    receiptNumber?: string;
    transactionId?: string;
    chequeNumber?: string;
    bankName?: string;
    milestoneId?: string;
    notes?: string;
  }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/payments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to record payment');
    }
    const result = await response.json();
    return result.data;
  },

  // Update a payment
  async updatePayment(paymentId: string, data: {
    amount?: number;
    paymentDate?: string;
    paymentMethod?: string;
    invoiceNumber?: string;
    receiptNumber?: string;
    transactionId?: string;
    chequeNumber?: string;
    bankName?: string;
    milestoneId?: string;
    notes?: string;
  }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/payments/${paymentId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to update payment');
    }
    const result = await response.json();
    return result.data;
  },

  // Delete a payment
  async deletePayment(paymentId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/payments/${paymentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete payment');
    }
  },

  // Verify a payment
  async verifyPayment(paymentId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/payments/${paymentId}/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to verify payment');
    }
    const result = await response.json();
    return result.data;
  },

  // Process a refund
  async refundPayment(paymentId: string, data: { amount?: number; reason: string }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/payments/${paymentId}/refund`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to process refund');
    }
    const result = await response.json();
    return result.data;
  },

  // Get payment milestones for a lead
  async getLeadMilestones(leadId: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/milestones`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch milestones');
    }
    const result = await response.json();
    return result.data;
  },

  // Create payment milestone(s)
  async createMilestone(leadId: string, data: {
    title: string;
    description?: string;
    amount: number;
    dueDate: string;
    percentage?: number;
  } | { milestones: Array<{
    title: string;
    description?: string;
    amount: number;
    dueDate: string;
    percentage?: number;
  }> }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/leads/${leadId}/milestones`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to create milestone');
    }
    const result = await response.json();
    return result.data;
  },

  // Update a milestone
  async updateMilestone(milestoneId: string, data: {
    title?: string;
    description?: string;
    amount?: number;
    dueDate?: string;
    percentage?: number;
  }): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/milestones/${milestoneId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to update milestone');
    }
    const result = await response.json();
    return result.data;
  },

  // Delete a milestone
  async deleteMilestone(milestoneId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/milestones/${milestoneId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to delete milestone');
    }
  },

  // ==========================================
  // Sales Analytics API (Sales Dashboard)
  // ==========================================

  // Get sales summary with period comparison
  async getSalesSummary(params?: {
    period?: SalesPeriod;
    date?: string;
  }): Promise<SalesSummaryResponse> {
    const urlParams = new URLSearchParams();
    if (params?.period) urlParams.append('period', params.period);
    if (params?.date) urlParams.append('date', params.date);

    const response = await fetch(`${API_BASE_URL}/analytics/sales/summary?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch sales summary'));
    }
    return response.json();
  },

  // Get sales breakdown by executive with rankings
  async getSalesByExecutive(params?: {
    period?: SalesPeriod;
    sortBy?: 'revenue' | 'dealsWon' | 'conversionRate';
    limit?: number;
  }): Promise<SalesByExecutiveResponse> {
    const urlParams = new URLSearchParams();
    if (params?.period) urlParams.append('period', params.period);
    if (params?.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params?.limit) urlParams.append('limit', params.limit.toString());

    const response = await fetch(`${API_BASE_URL}/analytics/sales/by-executive?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch sales by executive'));
    }
    return response.json();
  },

  // Get sales trends for charts
  async getSalesTrends(params?: {
    range?: TrendRange;
    granularity?: TrendGranularity;
    comparePrevious?: boolean;
  }): Promise<SalesTrendsResponse> {
    const urlParams = new URLSearchParams();
    if (params?.range) urlParams.append('range', params.range);
    if (params?.granularity) urlParams.append('granularity', params.granularity);
    if (params?.comparePrevious !== undefined) urlParams.append('comparePrevious', params.comparePrevious.toString());

    const response = await fetch(`${API_BASE_URL}/analytics/sales/trends?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch sales trends'));
    }
    return response.json();
  },

  // Get conversion funnel data
  async getConversionFunnel(params?: {
    period?: SalesPeriod;
  }): Promise<ConversionFunnelResponse> {
    const urlParams = new URLSearchParams();
    if (params?.period) urlParams.append('period', params.period);

    const response = await fetch(`${API_BASE_URL}/analytics/conversion-funnel?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch conversion funnel'));
    }
    return response.json();
  },

  // Get leaderboard data
  async getLeaderboard(params?: {
    period?: SalesPeriod;
    metric?: LeaderboardMetric;
    limit?: number;
  }): Promise<LeaderboardResponse> {
    const urlParams = new URLSearchParams();
    if (params?.period) urlParams.append('period', params.period);
    if (params?.metric) urlParams.append('metric', params.metric);
    if (params?.limit) urlParams.append('limit', params.limit.toString());

    const response = await fetch(`${API_BASE_URL}/analytics/leaderboard?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch leaderboard'));
    }
    return response.json();
  },

  // Get quick stats for dashboard header
  async getQuickStats(): Promise<QuickStatsResponse> {
    const response = await fetch(`${API_BASE_URL}/analytics/quick-stats`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch quick stats'));
    }
    return response.json();
  },

  // ==========================================
  // Sales Dashboard (Gym-style consolidated)
  // ==========================================

  // Get consolidated sales dashboard data
  async getSalesDashboardData(params?: {
    period?: DashboardPeriod;
  }): Promise<SalesDashboardResponse> {
    const url = new URL(`${API_BASE_URL}/sales-dashboard`);
    if (params?.period) {
      url.searchParams.append('period', params.period);
    }

    const response = await fetch(url.toString(), {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch sales dashboard data'));
    }
    return response.json();
  },

  // Get quick stats for sales dashboard
  async getSalesDashboardQuickStats(): Promise<SalesDashboardQuickStatsResponse> {
    const response = await fetch(`${API_BASE_URL}/sales-dashboard/quick-stats`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch sales dashboard quick stats'));
    }
    return response.json();
  },

  // Export sales dashboard data as CSV
  async exportSalesDashboardData(period: DashboardPeriod = 'month'): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/sales-dashboard/export?period=${period}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to export sales dashboard data');
    }
    return response.blob();
  },

  // ==========================================
  // Daily KPI endpoints
  // ==========================================

  async getDailyKPI(params?: { date?: string; userId?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.date) searchParams.set('date', params.date);
    if (params?.userId) searchParams.set('userId', params.userId);
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/daily-kpi${query ? '?' + query : ''}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch daily KPI data'));
    }
    return response.json();
  },

  async getDailyKPITeam(params?: { date?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.date) searchParams.set('date', params.date);
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/daily-kpi/team${query ? '?' + query : ''}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch team KPI data'));
    }
    return response.json();
  },

  async getDailyKPISources() {
    const response = await fetch(`${API_BASE_URL}/daily-kpi/sources`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(getFriendlyErrorMessage(response.status, 'Failed to fetch lead source stats'));
    }
    return response.json();
  },

  async logDailyActivity(data: { type: string; leadId: string; notes?: string; duration?: number }) {
    const response = await fetch(`${API_BASE_URL}/daily-kpi/log-activity`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to log activity');
    }
    return response.json();
  },

  async getDailyKPITeamReport(params?: { date?: string }) {
    const q = new URLSearchParams();
    if (params?.date) q.set('date', params.date);
    const res = await fetch(`${API_BASE_URL}/daily-kpi/team-report${q.toString() ? '?' + q : ''}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to fetch team daily report');
    return res.json();
  },

  async logCall(data: { leadId: string; outcome: string; durationMinutes?: number; notes?: string }) {
    const res = await fetch(`${API_BASE_URL}/daily-kpi/log-call`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to log call');
    return res.json();
  },

  async exportDailyReport(params: { date?: string; format: 'csv' | 'whatsapp' }): Promise<string> {
    const q = new URLSearchParams();
    if (params.date) q.set('date', params.date);
    q.set('format', params.format);
    const res = await fetch(`${API_BASE_URL}/daily-kpi/export?${q}`, {
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to export daily report');
    return res.text();
  },

  // Finance Pulse — CEO/GROUP_MANAGER only
  async getFinancePulse(params: { dateFrom?: string; dateTo?: string } = {}): Promise<any> {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    const res = await fetch(`${API_BASE_URL}/payments/pulse?${query}`, { headers: getAuthHeaders() });
    return res.json();
  },

  // Monthly Trend — 6-month booked vs collected (RBAC-scoped server-side)
  async getMonthlyTrend(params: { months?: number } = {}): Promise<any> {
    const query = new URLSearchParams();
    query.set('months', String(params.months ?? 6));
    const res = await fetch(`${API_BASE_URL}/finance/monthly-trend?${query}`, {
      headers: getAuthHeaders()
    });
    if (!res.ok) {
      throw new Error(getFriendlyErrorMessage(res.status, 'Failed to fetch monthly trend'));
    }
    return res.json();
  },

  // Missed Follow-ups — RBAC applied server-side
  async getMissedFollowups(params: { page?: number; limit?: number; assignedTo?: string; subDisposition?: string } = {}): Promise<any> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.assignedTo) query.set('assignedTo', params.assignedTo);
    if (params.subDisposition) query.set('subDisposition', params.subDisposition);
    const res = await fetch(`${API_BASE_URL}/leads/missed-followups?${query}`, { headers: getAuthHeaders() });
    return res.json();
  },

  // Disposition history for a lead
  async getDispositionHistory(leadId: string, params: { page?: number; limit?: number } = {}): Promise<any> {
    if (!leadId) return { data: { history: [], total: 0 } };
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    const res = await fetch(`${API_BASE_URL}/leads/${leadId}/disposition-history?${query}`, { headers: getAuthHeaders() });
    return res.json();
  },
};

// ==========================================
// Standalone exports for Payment Tracking
// ==========================================
export const getFinanceDashboard = api.getFinanceDashboard.bind(api);
export const exportFinanceData = api.exportFinanceData.bind(api);
export const getLeadPayments = api.getLeadPayments.bind(api);
export const recordPayment = api.recordPayment.bind(api);
export const updatePayment = api.updatePayment.bind(api);
export const deletePayment = api.deletePayment.bind(api);
export const verifyPayment = api.verifyPayment.bind(api);
export const refundPayment = api.refundPayment.bind(api);
export const getLeadMilestones = api.getLeadMilestones.bind(api);
export const createMilestone = api.createMilestone.bind(api);
export const updateMilestone = api.updateMilestone.bind(api);
export const deleteMilestone = api.deleteMilestone.bind(api);

// ==========================================
// Standalone exports for Sales Analytics
// ==========================================
export const getSalesSummary = api.getSalesSummary.bind(api);
export const getSalesByExecutive = api.getSalesByExecutive.bind(api);
export const getSalesTrends = api.getSalesTrends.bind(api);
export const getConversionFunnel = api.getConversionFunnel.bind(api);
export const getLeaderboard = api.getLeaderboard.bind(api);
export const getQuickStats = api.getQuickStats.bind(api);

// ==========================================
// Standalone exports for Sales Dashboard (Gym-style)
// ==========================================
export const getSalesDashboardData = api.getSalesDashboardData.bind(api);
export const getSalesDashboardQuickStats = api.getSalesDashboardQuickStats.bind(api); 